import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Folder, File, ArrowLeft, Home } from 'lucide-react';
import { FileSystemNavigator, FileSystemItem } from '../../utils/filesystem';
import { cn } from '../../lib/utils';
import { supabase } from '@os/integrations/supabase/client';

interface SaveAsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (path: string, fileName: string) => void;
  currentFileName: string;
  supportedExtensions?: string[];
}

const FILE_TYPE_CATEGORIES = {
  'Web': ['html', 'css', 'scss', 'js', 'jsx', 'ts', 'tsx'],
  'Programming': ['py', 'java', 'cpp', 'c', 'cs', 'php'],
  'Data': ['json', 'xml', 'yaml', 'yml', 'toml', 'sql'],
  'Shell': ['sh', 'bat', 'ps1'],
  'Config': ['ini', 'cfg', 'conf'],
  'Documentation': ['md', 'txt', 'rst'],
  'All': ['*']
};

export const SaveAsDialog: React.FC<SaveAsDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  currentFileName,
  supportedExtensions = ['txt']
}) => {
  const [navigator] = useState(new FileSystemNavigator());
  const [currentPath, setCurrentPath] = useState('C:\\'); // will resolve to user's Documents on open
  const [pathInput, setPathInput] = useState('C:\\');
  const [items, setItems] = useState<FileSystemItem[]>([]);
  const [fileName, setFileName] = useState(currentFileName);
  const [selectedExtension, setSelectedExtension] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('All');

  // Helper: resolve Documents path for current user
  const resolveDocumentsPath = async (): Promise<string> => {
    const { data } = await supabase.auth.getSession();
    const user = data.session?.user;
    let username = 'User';
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('username,email')
        .eq('id', user.id)
        .maybeSingle();
      const candidate =
        profile?.username ||
        (profile?.email || user.email || 'User').split('@')[0] ||
        'User';
      username = (candidate.replace(/[^a-zA-Z0-9]/g, '') || 'User');
    }
    return `C:\\User\\${username}\\Documents`;
  };

  // Initialize dialog when it opens
  useEffect(() => {
    if (isOpen) {
      (async () => {
        const docs = await resolveDocumentsPath();
        setCurrentPath(docs);
        setPathInput(docs);
        navigator.navigate(docs);
        refreshItems(docs);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Set initial filename and extension only when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Set default extension from current filename or first supported extension
      const currentExt = currentFileName.split('.').pop();
      if (currentExt && supportedExtensions.includes(currentExt)) {
        setSelectedExtension(currentExt);
      } else {
        setSelectedExtension(supportedExtensions[0]);
      }
      // Remove extension from filename display
      const nameWithoutExt = currentFileName.includes('.') 
        ? currentFileName.split('.').slice(0, -1).join('.')
        : currentFileName;
      setFileName(nameWithoutExt === 'untitled' ? '' : nameWithoutExt);
    }
  }, [isOpen]); // Only depend on isOpen, not currentFileName

  useEffect(() => {
    if (isOpen) {
      refreshItems(currentPath);
    }
  }, [fileTypeFilter, isOpen, currentPath]);

  const refreshItems = (path?: string) => {
    const activePath = path || currentPath;
    const pathItems = navigator.getItemsInPath(activePath);
    // Filter items based on selected file type filter
    const filteredItems = pathItems.filter(item => {
      if (item.type === 'folder') return true;
      if (fileTypeFilter === 'All') return true;
      
      const categoryExtensions = FILE_TYPE_CATEGORIES[fileTypeFilter as keyof typeof FILE_TYPE_CATEGORIES];
      return categoryExtensions.includes(item.extension || '');
    });
    setItems(filteredItems);
  };

  const handleFolderClick = (folder: FileSystemItem) => {
    setCurrentPath(folder.path);
    setPathInput(folder.path);
    navigator.navigate(folder.path);
  };

  const handleParentClick = () => {
    const parentPath = navigator.getParentPath(currentPath);
    if (parentPath !== currentPath) {
      setCurrentPath(parentPath);
      setPathInput(parentPath);
      navigator.navigate(parentPath);
    }
  };

  const handleGoHome = async () => {
    const docs = await resolveDocumentsPath();
    setCurrentPath(docs);
    setPathInput(docs);
    navigator.navigate(docs);
    refreshItems(docs);
  };

  const handlePathEnter = () => {
    const target = (pathInput || '').trim();
    if (!target) return;
    setCurrentPath(target);
    navigator.navigate(target);
    refreshItems(target);
  };

  const handleSave = () => {
    if (fileName.trim() && selectedExtension) {
      const finalFileName = `${fileName}.${selectedExtension}`;
      const fullPath = `${currentPath}\\${finalFileName}`;
      onSave(fullPath, finalFileName);
    }
  };

  const handleFileNameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border border-border rounded shadow-xl w-[500px] h-[400px] flex flex-col">
        {/* Header */}
        <div className="border-b border-border p-4">
          <h3 className="text-lg font-semibold">Save As</h3>
        </div>

        {/* Navigation */}
        <div className="border-b border-border p-2 flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleParentClick}
            disabled={currentPath === 'C:\\'}
          >
            <ArrowLeft size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleGoHome}
            title="Go to Documents"
          >
            <Home size={16} />
          </Button>
          <Input
            value={pathInput}
            onChange={(e) => setPathInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handlePathEnter();
              }
            }}
            className="flex-1 font-mono h-8"
            placeholder="Type a path like C:\\User\\Name\\Documents"
          />
          <Select value={fileTypeFilter} onValueChange={(value) => {
            setFileTypeFilter(value);
            refreshItems();
          }}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(FILE_TYPE_CATEGORIES).map(category => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* File Browser */}
        <div className="flex-1 overflow-auto p-2">
          <div className="space-y-1">
            {items.map((item) => (
              <div
                key={item.id}
                className={cn(
                  "flex items-center gap-2 p-2 rounded hover:bg-accent cursor-pointer",
                  item.type === 'folder' ? 'text-blue-600' : 'text-foreground'
                )}
                onClick={() => {
                  if (item.type === 'folder') {
                    handleFolderClick(item);
                  } else if (item.type === 'file' && supportedExtensions.includes(item.extension || '')) {
                    const nameWithoutExt = item.name.split('.').slice(0, -1).join('.');
                    setFileName(nameWithoutExt);
                    setSelectedExtension(item.extension || '');
                  }
                }}
              >
                {item.type === 'folder' ? (
                  <Folder size={16} />
                ) : (
                  <File size={16} />
                )}
                <span className="text-sm">{item.name}</span>
                {item.type === 'file' && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    {item.size}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* File Name Input */}
        <div className="border-t border-border p-4">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">File name:</label>
              <div className="flex gap-2">
                <Input
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  onKeyPress={handleFileNameKeyPress}
                  placeholder="Enter file name"
                  autoFocus
                  className="flex-1"
                />
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">.</span>
                  <Input
                    value={selectedExtension}
                    onChange={(e) => setSelectedExtension(e.target.value)}
                    placeholder="txt"
                    className="w-24 pl-6"
                  />
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Full name: {fileName.trim()}.{selectedExtension}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave}
                disabled={!fileName.trim() || !selectedExtension}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};